import { WORLD_SNAP, DEFAULT_H, MATERIALS } from "../constants";
import { Geo } from "../utils/geo";
import { snapWP, intP, mkId } from "../utils/coords";
import { addEdgePrim, removeEdgePrim, splitEdgePrim, getOrCreate, mkEmpty } from "./dcel";
import { healGraph } from "./heal";
import { solveConstraints } from "./constraintSolver";

export function mutAddWall(rawA, rawB, edgeType, thickness, height, materialId, st) {
  const ptA = intP(snapWP(rawA)), ptB = intP(snapWP(rawB));
  const { id: startId, nodes: n1 } = getOrCreate(ptA, st.nodes);
  let s = { ...st, nodes: n1 };
  const { id: endId, nodes: n2 } = getOrCreate(ptB, s.nodes);
  s = { ...s, nodes: n2 };
  if (startId === endId) return st;
  if (Geo.dsq(s.nodes[startId], s.nodes[endId]) < WORLD_SNAP * WORLD_SNAP * 0.1) return st;

  const sn = s.nodes[startId];
  const ixs = [];
  for (const e of Object.values(s.edges)) {
    const ab = s.halfEdges[e.halfEdgeA], ba = s.halfEdges[e.halfEdgeB];
    if (!ab || !ba) continue;
    if (ab.originId === startId || ba.originId === startId || ab.originId === endId || ba.originId === endId) continue;
    const nA = s.nodes[ab.originId], nB = s.nodes[ba.originId];
    if (!nA || !nB) continue;
    const ix = Geo.sx(sn, s.nodes[endId], nA, nB);
    if (!ix || ix.t < 0.005 || ix.t > 0.995) continue;
    ixs.push({ t: ix.t, u: ix.u, x: Math.round(ix.x), y: Math.round(ix.y), eId: e.id });
  }
  ixs.sort((a, b) => a.t - b.t);

  let cur = startId;
  for (const ix of ixs) {
    const e = s.edges[ix.eId]; if (!e) continue;
    let mid;
    if (ix.u < 0.005) mid = s.halfEdges[e.halfEdgeA]?.originId;
    else if (ix.u > 0.995) mid = s.halfEdges[e.halfEdgeB]?.originId;
    else { const res = splitEdgePrim(ix.eId, { x: ix.x, y: ix.y }, s); s = res.st; mid = res.mid; }
    if (!mid || mid === cur) continue;
    s = addEdgePrim(cur, mid, edgeType, thickness, height, s);
    const newEId = Object.keys(s.edges)[Object.keys(s.edges).length - 1];
    if (s.edges[newEId]) s = { ...s, edges: { ...s.edges, [newEId]: { ...s.edges[newEId], materialId: materialId || "BRICK" } } };
    cur = mid;
  }
  if (endId !== cur) s = addEdgePrim(cur, endId, edgeType, thickness, height, s);
  const lastEId = Object.keys(s.edges)[Object.keys(s.edges).length - 1];
  if (s.edges[lastEId]) s = { ...s, edges: { ...s.edges, [lastEId]: { ...s.edges[lastEId], materialId: materialId || "BRICK" } } };

  const mutAABB = { minX: Math.min(ptA.x, ptB.x), maxX: Math.max(ptA.x, ptB.x), minY: Math.min(ptA.y, ptB.y), maxY: Math.max(ptA.y, ptB.y) };
  const { state, errors } = healGraph(s, mutAABB);
  const op = { type: "ADD_WALL", payload: { a: ptA, b: ptB }, ts: Date.now() };
  return { ...state, opLog: [...(state.opLog || []).slice(-99), op], _errors: errors };
}

export function mutMoveNode(nId, newMm, st) {
  const p = intP(snapWP(newMm)), n = st.nodes[nId]; if (!n) return st;
  const mutAABB = { minX: Math.min(n.x, p.x), maxX: Math.max(n.x, p.x), minY: Math.min(n.y, p.y), maxY: Math.max(n.y, p.y) };
  let s = { ...st, nodes: { ...st.nodes, [nId]: { ...n, ...p } } };
  // Solve constraints — anchor the dragged node, let others adjust
  s = solveConstraints(s, [nId]);
  const { state, errors } = healGraph(s, mutAABB);
  return { ...state, _errors: errors };
}

export function mutDeleteEdge(eId, st) {
  let s = removeEdgePrim(eId, st);
  const nn = { ...s.nodes };
  for (const [id, n] of Object.entries(nn)) if (n.halfEdgeIds.length === 0) delete nn[id];
  s = { ...s, nodes: nn };
  const { state, errors } = healGraph(s);
  return { ...state, opLog: [...(state.opLog || []).slice(-99), { type: "DEL_EDGE", payload: { eId }, ts: Date.now() }], _errors: errors };
}

export function mutUpdateEdge(eId, props, st) {
  if (!st.edges[eId]) return st;
  const s = { ...st, edges: { ...st.edges, [eId]: { ...st.edges[eId], ...props } } };
  const { state, errors } = healGraph(s);
  return { ...state, _errors: errors };
}

export function mutResizeWall(eId, newLenMm, st) {
  const e = st.edges[eId]; if (!e) return st;
  const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
  if (!ab || !ba) return st;
  const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
  if (!nA || !nB) return st;
  const curLen = Geo.d(nA, nB); if (curLen < 1) return st;
  const scale = newLenMm / curLen;
  const newB = intP({ x: nA.x + (nB.x - nA.x) * scale, y: nA.y + (nB.y - nA.y) * scale });
  return mutMoveNode(ba.originId, newB, st);
}

export function mutSetRoom(faceId, rtype, rname, st) {
  return { ...st, rooms: { ...st.rooms, [faceId]: { type: rtype, name: rname || "" } } };
}

export function mutAddOpening(eId, preset, t, st) {
  if (!st.edges[eId]) return st;
  const id = mkId("op"), op = { id, edgeId: eId, t: t || 0.5, ...preset };
  return { ...st, openings: { ...st.openings, [id]: op }, opLog: [...(st.opLog || []).slice(-99), { type: "ADD_OPENING", payload: { eId }, ts: Date.now() }] };
}

export function mutDeleteOpening(opId, st) {
  const nop = { ...st.openings };
  delete nop[opId];
  return { ...st, openings: nop };
}

export function mutHeal(st) {
  const { state, errors } = healGraph(st);
  return { ...state, _errors: errors };
}

export function mutSetProject(fields, st) {
  return { ...st, project: { ...st.project, ...fields } };
}

export { mkEmpty };

// ─── OFFSET ───────────────────────────────────────────────────────────────────

/**
 * Compute the two endpoints of an edge's offset line.
 * side: "left" | "right" (relative to the direction A→B)
 *   left  = perpendicular 90° CCW (to the left when walking A→B)
 *   right = perpendicular 90° CW
 * Returns { ptA, ptB } in world-mm, or null if edge is invalid.
 */
export function getOffsetPoints(eId, distMm, side, st) {
  const e  = st.edges[eId]; if (!e) return null;
  const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
  if (!ab || !ba) return null;
  const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
  if (!nA || !nB) return null;
  const dx = nB.x - nA.x, dy = nB.y - nA.y;
  const L  = Math.sqrt(dx * dx + dy * dy);
  if (L < 1) return null;
  // Unit perpendicular — left = CCW = (-dy, dx) / L
  const sign = side === "right" ? -1 : 1;
  const nx = sign * (-dy) / L;
  const ny = sign *   dx  / L;
  return {
    ptA: { x: Math.round(nA.x + nx * distMm), y: Math.round(nA.y + ny * distMm) },
    ptB: { x: Math.round(nB.x + nx * distMm), y: Math.round(nB.y + ny * distMm) },
    nA, nB,
  };
}

/**
 * Offset a single wall edge — creates a parallel wall at distMm.
 * Inherits material, height, thickness from the source edge.
 * The new wall is NOT connected to the original (you can chain-offset a room).
 *
 * @param {string}  eId     — source edge id
 * @param {number}  distMm  — offset distance in mm (must be > 0)
 * @param {"left"|"right"} side — which side to offset to
 * @param {object}  st      — current DCEL state
 * @returns new state
 */
export function mutOffsetEdge(eId, distMm, side, st) {
  if (!distMm || distMm <= 0) return st;
  const pts = getOffsetPoints(eId, distMm, side, st);
  if (!pts) return st;
  const e = st.edges[eId];
  return mutAddWall(
    pts.ptA, pts.ptB,
    e.type || "wall",
    e.thickness || 230,
    e.height    || 3000,
    e.materialId || "BRICK",
    st,
  );
}

/**
 * Offset an entire closed face (room) boundary — outward (positive dist)
 * or inward (negative dist). Creates a complete parallel loop.
 *
 * Internally: for each edge in the face loop, compute the offset segment,
 * then miter-join adjacent segments at shared corners.
 *
 * @param {string} faceId
 * @param {number} distMm   positive = outward, negative = inward
 * @param {object} st
 */
export function mutOffsetFace(faceId, distMm, st) {
  if (!distMm) return st;
  const face = st.faces[faceId]; if (!face || !face.loop || face.loop.length < 3) return st;

  // Build ordered list of nodes around the loop
  const pts = face.loop.map(hid => {
    const he = st.halfEdges[hid]; if (!he) return null;
    return st.nodes[he.originId];
  }).filter(Boolean);
  if (pts.length < 3) return st;

  // Determine winding — CCW = positive area → outward is left offset
  const area = Geo.area(pts);
  // For CCW winding (area > 0): outward = left perpendicular
  // For CW  winding (area < 0): outward = right perpendicular
  const sign = (area > 0 ? 1 : -1) * (distMm > 0 ? 1 : -1);
  const absDist = Math.abs(distMm);

  // Compute offset for each segment
  const n = pts.length;
  const segs = pts.map((p, i) => {
    const q  = pts[(i + 1) % n];
    const dx = q.x - p.x, dy = q.y - p.y;
    const L  = Math.sqrt(dx * dx + dy * dy);
    if (L < 1) return null;
    // Left perpendicular
    const nx = sign * (-dy) / L;
    const ny = sign *   dx  / L;
    return {
      ptA: { x: Math.round(p.x + nx * absDist), y: Math.round(p.y + ny * absDist) },
      ptB: { x: Math.round(q.x + nx * absDist), y: Math.round(q.y + ny * absDist) },
      orig_ptA: p, orig_ptB: q,
    };
  }).filter(Boolean);

  if (segs.length < 3) return st;

  // Miter-join: find intersection of adjacent segment lines to get clean corners
  const corners = segs.map((seg, i) => {
    const prev = segs[(i - 1 + segs.length) % segs.length];
    // Line-line intersection of prev segment's extended line and seg's extended line
    const ix = Geo.sx(
      prev.ptA, prev.ptB,
      seg.ptA,  seg.ptB,
    );
    // If parallel or very far miter, fall back to seg.ptA
    if (!ix) return seg.ptA;
    const miterDist = Math.sqrt((ix.x - seg.ptA.x) ** 2 + (ix.y - seg.ptA.y) ** 2);
    if (miterDist > absDist * 3) return seg.ptA; // cap miter at 3× offset distance
    return { x: Math.round(ix.x), y: Math.round(ix.y) };
  });

  // Add the mitered offset loop as walls — inherit material from first edge in loop
  let sourceEdge = null;
  for (const hid of face.loop) {
    const he = st.halfEdges[hid]; if (!he) continue;
    const eid = he.edgeId || Object.values(st.edges).find(e => e.halfEdgeA === hid || e.halfEdgeB === hid)?.id;
    if (eid && st.edges[eid]) { sourceEdge = st.edges[eid]; break; }
  }
  const mat   = sourceEdge?.materialId || "BRICK";
  const thick = sourceEdge?.thickness  || 230;
  const h     = sourceEdge?.height     || 3000;

  let s = st;
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    s = mutAddWall(a, b, "wall", thick, h, mat, s);
  }
  return s;
}

/**
 * mutMoveNodes — move multiple nodes simultaneously, maintaining topology.
 * Single undo step via commitDrag (caller uses beginDrag/commitDrag).
 *
 * @param {Array<{id, x, y}>} nodePositions — final world-mm positions
 * @param {object} st
 */
export function mutMoveNodes(nodePositions, st) {
  const newNodes = { ...st.nodes };
  const movedIds = [];
  for (const { id, x, y } of nodePositions) {
    const n = newNodes[id]; if (!n) continue;
    newNodes[id] = { ...n, x: Math.round(x), y: Math.round(y) };
    movedIds.push(id);
  }
  let s = { ...st, nodes: newNodes };
  // Anchor all explicitly moved nodes so solver adjusts connected geometry
  s = solveConstraints(s, movedIds);
  const { state, errors } = healGraph(s);
  return { ...state, _errors: errors };
}
